import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Location } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Navigation from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import Fahrzeuge from './pages/Fahrzeuge';
import Kontakt from './pages/Kontakt';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import LoadingScreen from './components/LoadingScreen';
import Appointment from './pages/Appointment';
import About from './pages/About';
// Blog/News disabled for launch — re-enable by uncommenting these + the routes/links below.
// import News from './pages/News';
// import BlogPost from './pages/BlogPost';
// import Admin from './pages/Admin';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { useNarrowMobile } from './hooks/useNarrowMobile';
import { ConsentProvider } from './contexts/ConsentContext';
import ConsentBanner from './components/ConsentBanner';

// Scroll progress component — uses scaleX on a compositor layer instead of
// updating gradient stops (avoids repainting the full bar every scroll tick).
const ScrollProgress: React.FC = () => {
  const fillRef = useRef<HTMLDivElement>(null);
  const narrowMobile = useNarrowMobile();

  useEffect(() => {
    if (narrowMobile) return undefined;

    let rafId: number;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (!fillRef.current) return;
        const winScroll = document.documentElement.scrollTop || document.body.scrollTop;
        const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        const ratio = height > 0 ? Math.min(1, Math.max(0, winScroll / height)) : 0;
        fillRef.current.style.transform = `scaleX(${ratio})`;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [narrowMobile]);

  if (narrowMobile) return null;

  return (
    <div className="scroll-progress" aria-hidden>
      <div ref={fillRef} className="scroll-progress-fill" />
    </div>
  );
};

// Scroll to top component
const BackToTopButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let rafId = 0;
    const toggleVisibility = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const y = window.pageYOffset;
        setIsVisible((prev) => {
          if (prev && y < 220) return false;
          if (!prev && y > 280) return true;
          return prev;
        });
      });
    };

    window.addEventListener('scroll', toggleVisibility, { passive: true });
    toggleVisibility();
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      {isVisible && (
        <button
          onClick={scrollToTop}
          className="scroll-to-top"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </>
  );
};

/** Avoid wrapping Routes in AnimatePresence on narrow viewports — it interferes with smooth native scrolling on phones. */
const RoutedViews: React.FC<{ location: Location; narrowMobile: boolean }> = ({ location, narrowMobile }) => {
  const routes = (
    <Routes location={location} key={location.pathname}>
      <Route path="/" element={<Home />} />
      <Route path="/leistungen" element={<Services />} />
      <Route path="/fahrzeuge" element={<Fahrzeuge />} />
      <Route path="/kontakt" element={<Kontakt />} />
      <Route path="/ueber-uns" element={<About />} />
      {/* Blog/News disabled for launch — re-enable with the imports above.
      <Route path="/news" element={<News />} />
      <Route path="/news/:slug" element={<BlogPost />} />
      <Route path="/admin" element={<Admin />} /> */}
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/appointment" element={<Appointment />} />
    </Routes>
  );

  if (narrowMobile) return routes;

  return (
    <AnimatePresence mode="wait" onExitComplete={() => undefined}>
      {routes}
    </AnimatePresence>
  );
};

// Scroll handler component removed as it's handled by PageTransition
/* const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useLayoutEffect(() => {
    // Immediate scroll to top
    document.documentElement.scrollTo(0, 0);
    document.body.scrollTo(0, 0);
  }, [pathname]);

  return null;
}; */

// App content component that uses router hooks
const AppContent: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isHomePage, setIsHomePage] = useState(true);
  const location = useLocation();
  const narrowMobile = useNarrowMobile();

  useEffect(() => {
    // Prevent browser's default scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    setIsHomePage(location.pathname === '/');
  }, [location.pathname]);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* <ScrollToTop /> */}
      <ScrollProgress />
      <BackToTopButton />
      <LoadingScreen onLoadingComplete={handleLoadingComplete} />
      <div className={`app-content ${isLoading ? 'loading' : ''}`}>
        <Navigation isHomePage={isHomePage} />
        <RoutedViews location={location} narrowMobile={narrowMobile} />
        <Footer />
      </div>
      <ConsentBanner />
    </>
  );
};

// Main App component
const App: React.FC = () => {
  const basename = (process.env.PUBLIC_URL || '').replace(/\/$/, '') || undefined;
  return (
    <ConsentProvider>
      <Router basename={basename}>
        <AppContent />
      </Router>
    </ConsentProvider>
  );
};

export default App; 